import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SERVICES_INDEX } from "@/lib/data";
import { useLang } from "@/lib/i18n";
import { useHreflang, useSeo } from "@/components/seo/Seo";

const ease = [0.22, 1, 0.36, 1];

const FadeIn = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Services() {
  const { t, lang } = useLang();
  useSeo({ lang, title: `${t("sv.eyebrow")} | MILES`, description: t("sv.sub"), path: `/${lang}/services`, type: "services", id: "" });
  useHreflang({ fr: "/fr/services", en: "/en/services", de: "/de/services" });
  return (
    <div className="min-h-screen bg-white text-ink" data-testid="services-page">
      <Navbar />

      <section className="bg-gradient-to-b from-miles-ice/60 to-white pb-16 pt-32 lg:pb-20 lg:pt-40" data-testid="services-hero">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={16}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{t("sv.eyebrow")}</p>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] sm:text-5xl lg:text-6xl">
              {t("sv.h1a")}
              <br />
              <span className="font-light text-slate/55">{t("sv.h1b")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-slate sm:text-lg">{t("sv.sub")}</p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24 pt-4 lg:pb-32" data-testid="services-grid">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
            {SERVICES_INDEX.map((service, i) => {
              const item = t(`sv.items.${service.id}`);
              return (
                <FadeIn key={service.id} delay={(i % 3) * 0.08} className="h-full">
                  <Link to={service.href} data-testid={`service-card-${service.id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-ink/10 bg-white transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1.5 hover:border-miles/35 hover:shadow-[0_30px_70px_-38px_rgba(36,87,255,0.35)]">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={service.image} alt={item.name} loading="lazy"
                        className="img-clean absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-miles-deep/45 via-transparent to-transparent" />
                      <span className="absolute left-5 top-5 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-white/90">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="flex flex-1 items-start justify-between gap-4 p-6">
                      <div>
                        <h2 className="text-xl font-semibold tracking-tight text-ink">{item.name}</h2>
                        <p className="mt-2 text-sm leading-relaxed text-slate">{item.desc}</p>
                      </div>
                      <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cloud text-ink transition-[background-color,color,transform] duration-300 group-hover:-translate-y-0.5 group-hover:bg-miles group-hover:text-white">
                        <ArrowUpRight className="h-4 w-4" strokeWidth={1.5} />
                      </span>
                    </div>
                  </Link>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-miles-deep py-20 lg:py-28" data-testid="services-final-section">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-miles/25 blur-3xl" />
        <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-5 sm:px-8">
          <FadeIn>
            <h2 className="max-w-xl text-3xl font-semibold leading-[1.05] tracking-[-0.04em] text-white sm:text-4xl">
              {t("sv.final_h2")}
            </h2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-white/70">{t("sv.final_p")}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <Link to="/reservation" data-testid="services-final-cta"
              className="inline-flex items-center gap-2 rounded-full bg-miles px-8 py-4 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(36,87,255,0.4)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
              {t("sv.final_cta")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
